/**
 * One row per expert per job from the expert_job_metrics collection.
 * This neutral model is consumed by expert, admin, and operations workflows.
 */
export interface ExpertJobMetric {
  id: string;
  jobId: string;
  jobName: string;
  projectId: string;
  customerId: string;
  expertId: string;
  jobIsActive: boolean;
  isEligibleForTaskAssignment: boolean;
  jobDueAt: string | null;
  createdAt: string | null;
  lastUpdatedAt?: string | null;
  reviewedOn: string | null;
  deprecatedOn: string | null;
  hourlyRate: number;
  maxTaskLimit: number | null;
  taskPaymentType: string | null;
  taskType: string | null;
  taskCategory?: string | null;
  starRating: number | null;
  tasksAssigned: number;
  tasksCompleted: number;
  tasksFlagged: number;
  tasksSkipped: number;
  secondsWorked: number;
  earnings: number;
  /** Marks non-assignable accounting metrics created by campaign task payouts. */
  billingSource?: 'campaignTaskPayout' | null;
}

type Data = Record<string, unknown>;

const str = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const num = (value: unknown, fallback: number): number => (typeof value === 'number' ? value : fallback);
const numOrNull = (value: unknown): number | null => (typeof value === 'number' ? value : null);
const bool = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

export function mapExpertJobMetric(id: string, data: Data): ExpertJobMetric {
  return {
    id,
    jobId: str(data.jobId) ?? '',
    jobName: str(data.jobName) ?? '',
    projectId: str(data.projectId) ?? '',
    customerId: str(data.customerId) ?? '',
    expertId: str(data.expertId) ?? '',
    jobIsActive: bool(data.jobIsActive, true),
    isEligibleForTaskAssignment: bool(data.isEligibleForTaskAssignment, true),
    jobDueAt: str(data.jobDueAt) ?? null,
    createdAt: str(data.createdAt) ?? null,
    lastUpdatedAt: str(data.lastUpdatedAt) ?? null,
    reviewedOn: str(data.reviewedOn) ?? null,
    deprecatedOn: str(data.deprecatedOn) ?? null,
    hourlyRate: num(data.hourlyRate, 20_000_000),
    maxTaskLimit: numOrNull(data.maxTaskLimit),
    taskPaymentType: str(data.taskPaymentType) ?? null,
    taskType: str(data.taskType) ?? null,
    taskCategory: str(data.taskCategory) ?? null,
    starRating: numOrNull(data.starRating),
    tasksAssigned: num(data.tasksAssigned, 0),
    tasksCompleted: num(data.tasksCompleted, 0),
    tasksFlagged: num(data.tasksFlagged, 0),
    tasksSkipped: num(data.tasksSkipped, 0),
    secondsWorked: num(data.secondsWorked, 0),
    earnings: num(data.earnings, 0),
    billingSource: data.billingSource === 'campaignTaskPayout' ? 'campaignTaskPayout' : null,
  };
}

export function isCampaignTaskPayoutMetric(metric: Pick<ExpertJobMetric, 'billingSource'>): boolean {
  return metric.billingSource === 'campaignTaskPayout';
}
